import styled from "styled-components";
import { Box, TitleComponent, CustomTable } from "ui-components";
import { HorizontalCard, Amount, Label } from "../../components/common";
import { CompanyType, FundingRoundT } from "ui-io/company-io";
import { Card } from "@blueprintjs/core";
import { calculateFundingAmount, getInvestorCount } from "../../utils";
import { TableComponent } from "../../components";
import * as t from "io-ts";
import moment from "moment";
import { formatLargeNumber } from "ui-lib";
import { useQuery } from "react-query";
import { getInvestorsById } from "ui-io/queries/get-investors-by-id";
import ValueWithSkeleton from "../../components/common/value-with-skeleton";
import { useMemo } from "react";
type Props = {
  currentCompany?: CompanyType;
};
const FundingsTableColumnIdIO = t.union([
  t.literal("date"),
  t.literal("stage"),
  t.literal("amount"),
  t.literal("investorIDs"),
]);
type FundingsTableColumnIdT = t.TypeOf<typeof FundingsTableColumnIdIO>;
const CompanyFundings = (props: Props) => {
  const { currentCompany } = props;
  const tableColumns: { id: FundingsTableColumnIdT; label: string }[] = [
    { id: "date", label: "Date" },
    { id: "stage", label: "Stage" },
    { id: "amount", label: "Amount" },
    { id: "investorIDs", label: "Investors" },
  ];
  const allInvestorIDs =
    currentCompany?.funding_rounds
      ?.flatMap((round) =>
        Array.isArray(round.investorIDs) ? round.investorIDs : [],
      )
      .filter(
        (id): id is string => typeof id === "string" && id.trim().length > 0,
      ) ?? [];

  const hasInvestorIDs = allInvestorIDs.length > 0;

 
  const allInvestorDetails = useQuery({
    ...getInvestorsById({ invIDs: allInvestorIDs.join(",") }),
    enabled: hasInvestorIDs,
  });

  const investorDetails = useMemo(() => {
    if (!allInvestorDetails.data) return {};
    const invDetailsMap: { [key: string]: string } = {};
    allInvestorDetails.data.forEach((inv: any) => {
      if (!invDetailsMap[inv._id]) invDetailsMap[inv._id] = inv.name;
    });
    return invDetailsMap;
  }, [allInvestorDetails.data]);

  const formattedFundingRounds = useMemo(() => {
    if (
      !currentCompany?.funding_rounds ||
      currentCompany?.funding_rounds.length === 0
    )
      return [];
    return currentCompany?.funding_rounds?.map((round: FundingRoundT) => {
      const roundInvestors =
        round.investorIDs?.map((id: string) => investorDetails[id] || id) ||
        "N/A";
      return {
        fundingRoundID: round?.fundingRoundID,
        date: round.date ? moment(round.date).format("MMM YYYY") : "N/A",
        stage: round.stage || "N/A",
        amount: round.amount ? `£${formatLargeNumber(round.amount)}` : "N/A",
        investorIDs: roundInvestors,
      };
    });
  }, [currentCompany?.funding_rounds, investorDetails]);

  return (
    <>
      <div>
        <TitleComponent>Fundings</TitleComponent>
        <Box flexDir="row" justify="between" gap="large" mt={"medium"}>
          <HorizontalCard avatarIcon={"/images/total-funding.svg"}>
            <Amount className="mt-xs">
              £
              {currentCompany?.funding_rounds?.length &&
                calculateFundingAmount(currentCompany.funding_rounds)}
            </Amount>
            <StyledLabel>Funding Total</StyledLabel>
          </HorizontalCard>
          <HorizontalCard avatarIcon={"/images/funding-round.svg"}>
            <Amount className="mt-xs">
              {currentCompany?.funding_rounds?.length &&
                currentCompany.funding_rounds.length}
            </Amount>
            <StyledLabel>Funding Rounds</StyledLabel>
          </HorizontalCard>
          <HorizontalCard avatarIcon={"/images/investor.svg"}>
            <Amount className="mt-xs">
              {currentCompany?.funding_rounds?.length &&
                getInvestorCount(currentCompany.funding_rounds)}
            </Amount>
            <Label>Investors</Label>
          </HorizontalCard>
        </Box>
      </div>
      <div className="mt-xxlg">
        {/* <TitleComponent>Early Stage VC (Series A)</TitleComponent> */}
        <TitleComponent>Funding History</TitleComponent>
        <TableComponent minHeight="270px">
          <CustomTable>
            <thead>
              <tr>
                {tableColumns.map(({ id, label }) => (
                  <th key={id}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formattedFundingRounds?.map((row, index) => (
                <tr key={row.fundingRoundID || index}>
                  {tableColumns.map(({ id: columnId }) => (
                    <td key={columnId}>
                      {columnId == "investorIDs" ? (
                        hasInvestorIDs ? (
                          <ValueWithSkeleton
                            connection={allInvestorDetails}
                            children={
                              <StyledOrderedList>
                                {Array.isArray(row[columnId])
                                  ? row[columnId].map((e: any, i: number) => (
                                      <li key={i}>{e}</li>
                                    ))
                                  : "N/A"}
                              </StyledOrderedList>
                            }
                          />
                        ) : (
                          <StyledOrderedList>N/A</StyledOrderedList>
                        )
                      ) : (
                        row[columnId]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </CustomTable>
        </TableComponent>
      </div>
    </>
  );
};

export default CompanyFundings;

const StyledOrderedList = styled.ol`
  margin: 0;
  padding: 0;
  list-style-position: inside;
`;
const StyledLabel = styled(Label)`
  white-space: nowrap;
`;
